import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom'; // Import useLocation
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Tooltip,
  RadioGroup, // Added for goal type selection
  FormControlLabel, // Added for goal type selection
  Radio, // Added for goal type selection
  Chip // Added to display goal type
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh'; // Import RefreshIcon
// Assuming an AuthContext provides the token
// import { AuthContext } from '../../context/AuthContext'; // Adjust path as needed
import api from '../../services/api'; // Use the default export

const GoalManager = () => {
  // const { token } = useContext(AuthContext); // Get token from context
  const [token, setToken] = useState(localStorage.getItem('token')); // Temporary: Get token from localStorage
  const location = useLocation(); // Get location object

  const [goals, setGoals] = useState([]);
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [newGoalType, setNewGoalType] = useState('one_off'); // State for the new goal's type
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchGoals = useCallback(async () => {
    if (!token) {
      setError("Authentication token not found. Please log in.");
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const fetchedGoals = await api.getGoals(token);
      setGoals(fetchedGoals);
    } catch (err) {
      console.error("Error fetching goals:", err);
      setError(err.message || 'Failed to fetch goals.');
    } finally {
      setIsLoading(false);
    }
  }, [token]); // Dependency on token

  useEffect(() => {
    // Fetch goals whenever the component mounts or the location changes
    // (indicating navigation to this page) or token changes.
    if (token) {
        fetchGoals();
    } else {
        setError("Authentication token not found. Please log in.");
    }
  }, [fetchGoals, location, token]); // Add location and token to dependencies

  const handleCreateGoal = async (e) => {
    e.preventDefault(); // Prevent form submission page reload
    if (!newGoalDescription.trim()) {
      setError('Goal description cannot be empty.');
      return;
    }
    if (!token) {
      setError("Authentication token not found.");
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const goalData = {
        description: newGoalDescription.trim(),
        goalType: newGoalType // Include the selected goal type
      };
      await api.createGoal(goalData, token); // Pass the goalData object
      setNewGoalDescription(''); // Clear input field
      setNewGoalType('one_off'); // Reset goal type selector
      await fetchGoals(); // Refetch goals to show the new one
    } catch (err) {
      console.error("Error creating goal:", err);
      setError(err.message || 'Failed to create goal.');
      setIsLoading(false); // Ensure loading stops on error
    }
    // setIsLoading(false) will be called by fetchGoals in the success case
  };

  const handleUpdateGoalStatus = async (goalId, newStatus) => {
     if (!token) {
      setError("Authentication token not found.");
      return;
    }
    // No need to set isLoading here, as it's a quick update
    setError('');
    try {
      await api.updateGoal(goalId, { status: newStatus }, token);
      // Optimistic update (optional) or refetch
      await fetchGoals(); // Refetch to confirm update and get latest list
    } catch (err) {
      console.error("Error updating goal status:", err);
      setError(err.message || 'Failed to update goal status.');
    }
  };

  const handleDeleteGoal = async (goalId) => {
     if (!token) {
      setError("Authentication token not found.");
      return;
    }
    // Optional: Add a confirmation dialog here
    // setIsLoading(true); // Maybe show loading briefly
    setError('');
    try {
      await api.deleteGoal(goalId, token);
      await fetchGoals(); // Refetch goals after deletion
    } catch (err) {
      console.error("Error deleting goal:", err);
      setError(err.message || 'Failed to delete goal.');
      // setIsLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2, maxWidth: 600, margin: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" gutterBottom component="div" sx={{ mb: 0 }}> {/* Remove bottom margin */}
          My Therapeutic Goals
        </Typography>
        <Tooltip title="Refresh Goals">
          <IconButton onClick={fetchGoals} disabled={isLoading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Form for adding new goals */}
      <Box component="form" onSubmit={handleCreateGoal} sx={{ mb: 3 }}>
        <TextField
          label="New Goal Description"
          variant="outlined"
          fullWidth // Make text field take full width
          value={newGoalDescription}
          onChange={(e) => setNewGoalDescription(e.target.value)}
          size="small"
          sx={{ mb: 1 }} // Add margin below text field
          disabled={isLoading}
        />
        {/* Goal Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <RadioGroup
            row
            aria-label="goal-type"
            name="goal-type-radio-buttons-group"
            value={newGoalType}
            onChange={(e) => setNewGoalType(e.target.value)}
          >
            <FormControlLabel value="one_off" control={<Radio size="small"/>} label="One-off Task" />
            <FormControlLabel value="habit" control={<Radio size="small"/>} label="Ongoing Habit" />
          </RadioGroup>
        </FormControl>
        {/* Add Button */}
        <Button
          type="submit"
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          disabled={isLoading || !newGoalDescription.trim()}
          fullWidth // Make button take full width
        >
          Add Goal
        </Button>
      </Box>

      {/* Goal List */}
      {isLoading && !goals.length ? ( // Show loading only if initial fetch is happening
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {goals.length === 0 && !isLoading && (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
              You haven't added any goals yet.
            </Typography>
          )}
          {goals.map((goal) => (
            <React.Fragment key={goal._id}>
              <ListItem
                secondaryAction={
                  <Tooltip title="Delete Goal">
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteGoal(goal._id)}
                      disabled={isLoading}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                }
                sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}
              >
                <ListItemText
                  primary={goal.description}
                  secondary={`Created: ${new Date(goal.createdAt).toLocaleDateString()}`}
                  sx={{ flexGrow: 1, mr: 1 }} // Reduced margin
                />
                {/* Display Goal Type */}
                <Chip
                  label={goal.goalType === 'habit' ? 'Habit' : 'Task'}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 2, textTransform: 'capitalize' }}
                  color={goal.goalType === 'habit' ? 'secondary' : 'default'}
                 />
                {/* Status Dropdown */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id={`status-label-${goal._id}`}>Status</InputLabel>
                  <Select
                    labelId={`status-label-${goal._id}`}
                    id={`status-select-${goal._id}`}
                    value={goal.status}
                    label="Status"
                    onChange={(e) => handleUpdateGoalStatus(goal._id, e.target.value)}
                    disabled={isLoading}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="on_hold">On Hold</MenuItem>
                  </Select>
                </FormControl>
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default GoalManager;
